/**
 * A form asked for a paper besides the résumé — a photo, an ID proof, a
 * certificate. The person uploads it once here (Profile → Documents keeps
 * it); the answer is the stored document's id, which the run attaches to
 * that slot and only that slot. A document of the kind already on file is
 * offered first.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { toast } from '@/store/toast'
import { DOCUMENT_KIND_LABELS, type DocumentKind } from '@/types/api'

const ACCEPT: Partial<Record<DocumentKind, string>> = {
  photo: 'image/*',
  id_proof: 'image/*,.pdf',
  passport: 'image/*,.pdf',
  pan: 'image/*,.pdf',
}

/** The kind a question's words name, when the API did not say. */
export function documentKindOf(label: string): DocumentKind {
  const text = (label || '').toLowerCase()
  if (/\b(photo(graph)?|picture|headshot|selfie)\b/.test(text)) return 'photo'
  if (/\bpassport\b/.test(text)) return 'passport'
  if (/\bpan\b/.test(text)) return 'pan'
  if (/\b(aadhaa?r|id proof|id card|identity|voter|driving licen[cs]e|national id)\b/.test(text)) return 'id_proof'
  if (/\b(mark ?sheet|transcript|grade sheet)\b/.test(text)) return 'marksheet'
  if (/\b(pay ?slip|salary slip)\b/.test(text)) return 'payslip'
  if (/\boffer letter\b/.test(text)) return 'offer_letter'
  if (/\b(relieving|experience) letter\b/.test(text)) return 'relieving_letter'
  if (/\bcertificate\b/.test(text)) return 'certificate'
  return 'other'
}

export function DocumentUploadField({
  id,
  kind,
  value,
  onChange,
  required,
}: {
  id: string
  kind: DocumentKind
  /** The chosen document's id, or empty. */
  value: string
  onChange: (documentId: string) => void
  required?: boolean
}) {
  const queryClient = useQueryClient()
  const documents = useQuery({ queryKey: ['profile-documents'], queryFn: profileApi.listDocuments })
  const ofKind = (documents.data ?? []).filter((d) => d.kind === kind)
  const chosen = ofKind.find((d) => d.id === value) ?? null
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  // A document of the kind already on file is the answer until the person picks another.
  useEffect(() => {
    if (!value && ofKind[0]) onChange(ofKind[0].id)
  }, [value, ofKind, onChange])
  const upload = useMutation({
    mutationFn: (file: File) => profileApi.uploadDocument(kind, file),
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    onSuccess: (doc) => {
      toast.success(`${DOCUMENT_KIND_LABELS[kind]} uploaded`, 'It is used on every form that asks for it.')
      onChange(doc.id)
      void queryClient.invalidateQueries({ queryKey: ['profile-documents'] })
      void queryClient.invalidateQueries({ queryKey: ['review-inbox'] })
    },
    onError: (error: ApiError) => toast.error('Could not upload the document', error.message),
  })
  const label = DOCUMENT_KIND_LABELS[kind]
  return (
    <div className="space-y-2">
      {chosen ? (
        <p className="text-[13px] text-ink">
          <span className="font-medium">{chosen.fileName}</span>
          <span className="text-ink-muted"> · your {label.toLowerCase()} on file; it is attached to the form</span>
        </p>
      ) : (
        <p className="text-[13px] text-ink-muted">
          No {label.toLowerCase()} on file yet{required ? ' — the form will not go without it' : ''}. Upload it once; it is kept for every form that asks.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={input}
          id={id}
          type="file"
          accept={ACCEPT[kind] ?? 'image/*,.pdf,.doc,.docx'}
          className="sr-only"
          aria-required={required}
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) upload.mutate(file)
            event.target.value = ''
          }}
        />
        <Button variant="secondary" size="sm" type="button" loading={busy} onClick={() => input.current?.click()}>
          {chosen ? `Replace ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
        </Button>
        {ofKind.length > 1 && (
          <select value={value} onChange={(event) => onChange(event.target.value)} className="field-box min-h-[36px] text-[13px]" aria-label={`Which ${label.toLowerCase()} to use`}>
            {ofKind.map((d) => (
              <option key={d.id} value={d.id}>
                {d.fileName}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}
